<template>
  <div class="flex flex-col gap-2">
    <empty-screen
      v-if="list.length === 0"
      class="my-5"
      mobile
      icon="facebook"
      :title="tr('No Facebook Pages found')"
      :flag-text="tr('Watch tutorial')"
      :description="tr('Your Meta profile is connected, but we couldn\'t find any Facebook Pages linked to it. Connect a Facebook Page to start automating. Optionally, you can also manage your connected Instagram accounts here.')"
      :path="tr('https://www.youtube.com/watch?v=OPuP9F7DQEY')"
    />
    <template v-else>
      <asset-select-list
        :search-term="searchTerm"
        :items="items"
        :searchable="list.length > 10"
        :disabled="disabled"
        :loading="loading"
        @update:search-term="(value) => searchTerm = value"
        @select="(group) => onSelect(group[0])"
      >
        <template #label="{ item }">
          <social-page :source-profiles="item" />
        </template>
      </asset-select-list>
      <empty-screen
        v-if="filteredList.length === 0"
        class="my-5"
        mobile
        icon="search"
        :title="tr('No results match your current search criteria')"
        :flag-text="tr('Modify search')"
        :description="tr('Currently there are no items that match your criteria. The items you\'re looking for might appear here later, or try different parameters to see currently available items.')"
      />
    </template>
  </div>
</template>

<script>
import SourceProfile from '@/entities/SourceProfile.js';
import SelectInputMixin from '@/mixins/SelectInput.js';
import AssetSelectList from '@/ui/components/AssetSelectList.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import SocialPage from '@/ui/components/SocialPage.vue';

const PRIMARY_PLATFORM = 'facebook';

export default {
  name: 'FacebookPageSelectInput',
  components: {
    AssetSelectList,
    EmptyScreen,
    SocialPage,
  },
  mixins: [ SelectInputMixin ],
  computed: {
    items() {
      return this.filteredList.map(group => ({
        key: group[0].external_id,
        value: group,
        selected: this.modelValue === group[0].external_id,
        disabled: this.isDisabled(group[0]),
        loading: this.isLoading(group[0]),
        status: this.statusLabel(group[0]),
        statusError: group[0].lost_access === true,
      }));
    },
    list() {
      const listItems = [];

      const sourceProfiles = this.$root.currentCompany.source_profiles;
      sourceProfiles.filter(sp => sp.platform === PRIMARY_PLATFORM).forEach(sp => {
        const group = [ sp ];

        const igSourceProfile = sourceProfiles.find(igSp => igSp.platform === 'instagram' && igSp.usable_with_this === sp.id);
        if (igSourceProfile) {
          group.push(igSourceProfile);
        }

        listItems.push(group);
      });

      const connectedProfiles = this.$root.loggedInUser.connected_profiles.filter(profile => profile.platform === PRIMARY_PLATFORM);
      connectedProfiles.forEach(profile => {
        if (!profile.details?.pages) {
          return;
        }

        profile.details.pages.forEach(item => {
          if (this.isAlreadyInCompany(PRIMARY_PLATFORM, item.id)) {
            return;
          }

          const fbSourceProfile = this.createSourceProfile(item, PRIMARY_PLATFORM);

          const group = [ fbSourceProfile ];

          if (item.instagram_business_account) {
            const igSourceProfile = this.createSourceProfile(item.instagram_business_account, 'instagram');
            group.push(igSourceProfile);
          }

          listItems.push(group);
        });
      });

      listItems.sort((a, b) => a[0].name.localeCompare(b[0].name));

      return listItems;
    },
  },
  methods: {
    getSearchName(item) {
      return item[0].name;
    },
    statusLabel(item) {
      if (item.lost_access === true) {
        return this.tr('Access terminated');
      }

      return this.modelValue === item.external_id ? this.tr('Active') : null;
    },
    isDisabled(item) {
      if (this.disabled) {
        return true;
      }

      if (this.modelValue === item.external_id) {
        return false;
      }

      return item.lost_access === true;
    },
    isLoading(item) {
      return this.loading && this.modelValue === item.external_id;
    },
    isAlreadyInCompany(platform, externalId) {
      return this.$root.currentCompany.source_profiles.some(sp => sp.platform === platform && sp.external_id === externalId);
    },
    createSourceProfile(rawItem, platform) {
      const profileData = {
        ...rawItem,
        platform,
        external_id: rawItem.id,
        id: null,
        details: rawItem.details ? { ...rawItem.details } : {},
      };

      if (platform === 'instagram') {
        profileData.details.facebook_page_id = rawItem.facebook_page_id
          || rawItem.page_id
          || rawItem.page?.id
          || profileData.details.facebook_page_id
          || null;
      }

      return new SourceProfile(profileData);
    },
  },
};
</script>
